import React, { useState, useEffect } from 'react';
import { HelpCircle, Send, MessageSquare, Clock, CheckCircle, AlertCircle, Plus, X, BookOpen, Search, ChevronRight, FileText, Bot, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import axios from 'axios';
import EmployeeHelpChat from './EmployeeHelpChat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizationSupportPortal = ({ apiCall, token, user, toast, defaultTab }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [activeTab, setActiveTab] = useState(defaultTab || 'ai-assistant');
  
  // Knowledge Base State
  const [kbArticles, setKbArticles] = useState([]);
  const [kbLoading, setKbLoading] = useState(true);
  const [kbSearch, setKbSearch] = useState('');
  const [kbCategory, setKbCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  
  const kbCategories = [
    { value: 'all', label: 'All Categories', icon: '📚' },
    { value: 'getting-started', label: 'Getting Started', icon: '🚀' },
    { value: 'authorizations', label: 'Authorizations & Services', icon: '📋' },
    { value: 'billing', label: 'Billing & Claims', icon: '💰' },
    { value: 'clinical', label: 'Clinical Tools', icon: '🏥' },
    { value: 'scheduling', label: 'Scheduling & Calendar', icon: '📅' },
    { value: 'telehealth', label: 'Telehealth', icon: '📹' },
    { value: 'parent-portal', label: 'Parent Portal', icon: '👨‍👩‍👧' },
    { value: 'security', label: 'Security & Compliance', icon: '🔒' }
  ];
  
  const [newTicket, setNewTicket] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });

  useEffect(() => {
    if (token) {
      fetchTickets();
      fetchKBArticles();
    }
  }, [token]);

  const fetchKBArticles = async () => {
    try {
      setKbLoading(true);
      const params = new URLSearchParams();
      if (kbCategory !== 'all') params.append('category', kbCategory);
      if (kbSearch) params.append('search', kbSearch);
      params.append('published', 'true');
      
      const response = await axios.get(`${API_URL}/api/knowledge-base/articles?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns data in response.data.data (array of articles)
      const articles = response.data.data || response.data.articles || [];
      setKbArticles(articles);
    } catch (error) {
      console.error('Error fetching KB articles:', error);
      setKbArticles([]);
    } finally {
      setKbLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchKBArticles();
    }
  }, [kbCategory, kbSearch]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/organization/support-tickets', 'GET', null, token);
      // console.log('📋 Org tickets API response:', response);
      
      // Handle double-wrapped response
      const ticketsData = response?.data?.data || response?.data || [];
      
      if (Array.isArray(ticketsData)) {
        setTickets(ticketsData);
      } else {
        console.warn('⚠️ Tickets data is not an array:', ticketsData);
        setTickets([]);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load support tickets');
      setTickets([]);
    }
    setLoading(false);
  };

  const handleSubmitTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      toast.error('Please fill in subject and description');
      return;
    }

    try {
      // console.log('📤 Submitting ticket:', newTicket);
      const response = await apiCall('/organization/support-tickets', 'POST', newTicket, token);
      // console.log('📥 Submit response:', response);
      
      if (response.success) {
        toast.success('✅ Support ticket submitted successfully! Our team will respond soon.');
        setShowNewTicketModal(false);
        setNewTicket({ subject: '', description: '', priority: 'medium', category: 'general' });
        fetchTickets();
      } else {
        toast.error(response.message || 'Failed to submit support ticket');
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit support ticket');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      new: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'New' },
      'in-progress': { color: 'bg-yellow-100 text-yellow-800', icon: MessageSquare, label: 'In Progress' },
      resolved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Resolved' },
      closed: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle, label: 'Closed' }
    };
    const { color, icon: Icon, label } = config[status] || config.new;
    return (
      <Badge className={color}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return <Badge className={config[priority] || config.medium}>{priority.toUpperCase()}</Badge>;
  };

  const openTickets = tickets.filter(t => t.status !== 'resolved' && t.status !== 'closed');
  const resolvedTickets = tickets.filter(t => t.status === 'resolved' || t.status === 'closed');

  // Filter KB articles based on search and category
  const filteredKBArticles = kbArticles.filter(article => {
    const matchesSearch = !kbSearch || 
      article.title.toLowerCase().includes(kbSearch.toLowerCase()) ||
      article.content.toLowerCase().includes(kbSearch.toLowerCase()) ||
      article.tags?.some(tag => tag.toLowerCase().includes(kbSearch.toLowerCase()));
    const matchesCategory = kbCategory === 'all' || article.category === kbCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">📚 Help & Support Center</h2>
              <p className="text-gray-700">
                Browse our Knowledge Base for guides and tutorials, or submit a support ticket.
              </p>
            </div>
            <Button
              onClick={() => setShowNewTicketModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              size="lg"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Support Ticket
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs: Assistant | Knowledge Base | Support Tickets */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3 w-[600px] bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl">
          <TabsTrigger value="ai-assistant" className="flex items-center gap-2">
            <Bot className="w-4 h-4" />
            <Sparkles className="w-3 h-3" />
            Assistant
          </TabsTrigger>
          <TabsTrigger value="knowledge-base" className="flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Knowledge Base
          </TabsTrigger>
          <TabsTrigger value="support-tickets" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Support Tickets
          </TabsTrigger>
        </TabsList>

        {/* Assistant Tab */}
        <TabsContent value="ai-assistant" className="mt-6">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <EmployeeHelpChat 
                token={token} 
                user={user} 
                isPlatformAdmin={user?.role === 'super_admin'}
                orgName={user?.organization_name || ''}
              />
            </div>
            <div className="lg:col-span-1 space-y-4">
              <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-cyan-600" />
                    Pro Tips
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">⌨️</span>
                    <p><strong>Cmd/Ctrl + K</strong> opens Global Search - faster than clicking around!</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">💬</span>
                    <p>Ask the assistant anything - it knows where everything is in CounselCore.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">📖</span>
                    <p>The Knowledge Base has detailed guides with screenshots.</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">🎫</span>
                    <p>Can't find what you need? Submit a support ticket and we'll help!</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Popular Questions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {[
                    "How do I write a progress note?",
                    "Where do I schedule sessions?",
                    "How do I see my pending tasks?",
                    "Where are the client intakes?",
                    "How do I export a report?",
                  ].map((q, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveTab('ai-assistant')}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <HelpCircle className="w-4 h-4 text-gray-400" />
                      {q}
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Knowledge Base Tab */}
        <TabsContent value="knowledge-base" className="mt-6">
          <div className="grid lg:grid-cols-4 gap-6">
            {/* Sidebar - Categories */}
            <div className="lg:col-span-1 space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Categories</CardTitle>
                </CardHeader>
                <CardContent className="p-2">
                  <div className="space-y-1">
                    {kbCategories.map(cat => (
                      <button
                        key={cat.value}
                        onClick={() => setKbCategory(cat.value)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                          kbCategory === cat.value 
                            ? 'bg-blue-100 text-blue-800 font-medium' 
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <span className="mr-2">{cat.icon}</span>
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content - Articles */}
            <div className="lg:col-span-3 space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search knowledge base..."
                  value={kbSearch}
                  onChange={(e) => setKbSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Selected Article View */}
              {selectedArticle ? (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <button 
                          onClick={() => setSelectedArticle(null)}
                          className="text-blue-600 hover:text-blue-800 text-sm mb-2 flex items-center gap-1"
                        >
                          ← Back to articles
                        </button>
                        <CardTitle>{selectedArticle.title}</CardTitle>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline">{selectedArticle.category}</Badge>
                          <span className="text-xs text-gray-500">
                            By {selectedArticle.author_name} • {new Date(selectedArticle.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none">
                      {selectedArticle.content.split('\n').map((line, i) => {
                        if (line.startsWith('# ')) return <h1 key={i} className="text-2xl font-bold mt-4 mb-2">{line.replace('# ', '')}</h1>;
                        if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-semibold mt-4 mb-2">{line.replace('## ', '')}</h2>;
                        if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-medium mt-3 mb-1">{line.replace('### ', '')}</h3>;
                        if (line.startsWith('- ')) return <li key={i} className="ml-4">{line.replace('- ', '')}</li>;
                        if (line.startsWith('| ')) return <div key={i} className="font-mono text-sm bg-gray-50 px-2 py-1">{line}</div>;
                        if (line.trim() === '') return <br key={i} />;
                        return <p key={i} className="mb-2">{line}</p>;
                      })}
                    </div>
                    {selectedArticle.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t">
                        {selectedArticle.tags.map((tag, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">#{tag}</Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                /* Articles List */
                <>
                  {kbLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-gray-500 mt-2">Loading articles...</p>
                    </div>
                  ) : filteredKBArticles.length === 0 ? (
                    <Card className="p-12 text-center">
                      <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-600 mb-2">No articles found</p>
                      <p className="text-sm text-gray-500">Try adjusting your search or category filter</p>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {filteredKBArticles.map(article => (
                        <Card 
                          key={article.id} 
                          className="hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => setSelectedArticle(article)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <FileText className="w-4 h-4 text-blue-600" />
                                  <h3 className="font-semibold text-gray-900">{article.title}</h3>
                                </div>
                                <p className="text-sm text-gray-600 line-clamp-2 ml-6">
                                  {article.content.substring(0, 150)}...
                                </p>
                                <div className="flex items-center gap-3 mt-2 ml-6">
                                  <Badge variant="outline" className="text-xs">{article.category}</Badge>
                                  <span className="text-xs text-gray-500">{article.view_count || 0} views</span>
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Support Tickets Tab */}
        <TabsContent value="support-tickets" className="mt-6">

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600">Open Tickets</div>
            <div className="text-3xl font-bold text-blue-600">{openTickets.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600">Resolved</div>
            <div className="text-3xl font-bold text-green-600">{resolvedTickets.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600">Total Tickets</div>
            <div className="text-3xl font-bold text-gray-900">{tickets.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Open Tickets */}
      <Card>
        <CardHeader>
          <CardTitle>Open Support Tickets</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading tickets...</div>
          ) : openTickets.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600 mb-4">No open support tickets</p>
              <Button onClick={() => setShowNewTicketModal(true)} variant="outline">
                Submit Your First Ticket
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {openTickets.map((ticket) => (
                <Card 
                  key={ticket.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">{ticket.subject}</h3>
                          {getStatusBadge(ticket.status)}
                          {getPriorityBadge(ticket.priority)}
                        </div>
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">{ticket.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>Ticket #{ticket.id.slice(0, 8)}</span>
                          <span>•</span>
                          <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                          {ticket.assigned_to_name && (
                            <>
                              <span>•</span>
                              <span>Assigned to: {ticket.assigned_to_name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Resolved Tickets */}
      {resolvedTickets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Resolved Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {resolvedTickets.map((ticket) => (
                <div 
                  key={ticket.id}
                  className="p-3 bg-gray-50 rounded-md hover:bg-gray-100 cursor-pointer"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{ticket.subject}</span>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Resolved on {new Date(ticket.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Knowledge Base Section */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-600" />
            Knowledge Base
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            Find answers to common questions and browse helpful articles
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <a 
              href="/knowledge-base?category=getting-started"
              className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
            >
              <div className="text-2xl mb-2">🚀</div>
              <div className="font-medium text-gray-900">Getting Started</div>
              <div className="text-xs text-gray-500 mt-1">Setup and onboarding guides</div>
            </a>
            
            <a 
              href="/knowledge-base?category=features"
              className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
            >
              <div className="text-2xl mb-2">✨</div>
              <div className="font-medium text-gray-900">Features</div>
              <div className="text-xs text-gray-500 mt-1">How to use platform features</div>
            </a>
            
            <a 
              href="/knowledge-base?category=technical"
              className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
            >
              <div className="text-2xl mb-2">🔧</div>
              <div className="font-medium text-gray-900">Technical</div>
              <div className="text-xs text-gray-500 mt-1">Troubleshooting and setup</div>
            </a>
            
            <a 
              href="/knowledge-base?category=account"
              className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
            >
              <div className="text-2xl mb-2">👤</div>
              <div className="font-medium text-gray-900">Account</div>
              <div className="text-xs text-gray-500 mt-1">Billing and account settings</div>
            </a>
          </div>
          
          <div className="mt-4 text-center">
            <a 
              href="/knowledge-base"
              className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
            >
              Browse All Articles
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* New Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold">Submit Support Ticket</h2>
                  <p className="text-sm text-gray-600 mt-1">Our support team will respond as soon as possible</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowNewTicketModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                    placeholder="Brief summary of your issue"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      className="w-full p-2 border rounded-md"
                      value={newTicket.category}
                      onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                    >
                      <option value="general">General Question</option>
                      <option value="technical">Technical Issue</option>
                      <option value="billing">Billing Question</option>
                      <option value="feature">Feature Request</option>
                      <option value="bug">Bug Report</option>
                      <option value="training">Training & Support</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="priority">Priority</Label>
                    <select
                      id="priority"
                      className="w-full p-2 border rounded-md"
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                    >
                      <option value="low">Low - Can wait</option>
                      <option value="medium">Medium - Normal</option>
                      <option value="high">High - Important</option>
                      <option value="urgent">Urgent - Blocking work</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                    rows={8}
                    placeholder="Please provide as much detail as possible about your issue or question..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Include steps to reproduce the issue, error messages, and screenshots if applicable
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <strong>Response Time:</strong> We aim to respond within 24 hours for normal priority tickets, 
                      and within 4 hours for urgent tickets.
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button variant="outline" onClick={() => setShowNewTicketModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSubmitTicket} className="bg-blue-600 hover:bg-blue-700">
                    <Send className="w-4 h-4 mr-2" />
                    Submit Ticket
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold">{selectedTicket.subject}</h2>
                  <p className="text-sm text-gray-600">Ticket #{selectedTicket.id.slice(0, 8)}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedTicket(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedTicket.status)}
                  {getPriorityBadge(selectedTicket.priority)}
                  <Badge variant="outline">{selectedTicket.category}</Badge>
                </div>

                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="text-sm text-gray-600 mb-2">Description:</div>
                  <p className="text-gray-900">{selectedTicket.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Submitted:</span>
                    <span className="ml-2 font-medium">{new Date(selectedTicket.created_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Last Updated:</span>
                    <span className="ml-2 font-medium">{new Date(selectedTicket.updated_at).toLocaleString()}</span>
                  </div>
                  {selectedTicket.assigned_to_name && (
                    <div>
                      <span className="text-gray-600">Assigned To:</span>
                      <span className="ml-2 font-medium">{selectedTicket.assigned_to_name}</span>
                    </div>
                  )}
                  {selectedTicket.responded_by && (
                    <div>
                      <span className="text-gray-600">Resolved By:</span>
                      <span className="ml-2 font-medium">{selectedTicket.responded_by}</span>
                    </div>
                  )}
                </div>

                {selectedTicket.response && (
                  <div className="bg-green-50 p-4 rounded-md border border-green-200">
                    <div className="text-sm font-semibold text-green-800 mb-2">Response from Support Team:</div>
                    <p className="text-gray-900">{selectedTicket.response}</p>
                  </div>
                )}

                <div className="flex gap-2 justify-end pt-4 border-t">
                  <Button variant="outline" onClick={() => setSelectedTicket(null)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default OrganizationSupportPortal;
